// Step 3 (self-consistent runs only): the electrostatics - gates, dielectrics, temperature and Fermi level - with
// the device seen from the side and from the top, gates in gold and dielectrics in blue, as in QuantumATK-like
// device builders. Gates are planes at a distance from the device (back / top gates below / above it, in-plane
// side gates for a split-gate constriction), each with its voltage.
import { Accordion, ActionIcon, Alert, Badge, Button, Card, Checkbox, Grid, Group, NumberInput, Select, SimpleGrid, Stack, Switch,
  Text, TextInput } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { ErrorBox } from "../../components/Errors";
import { Plot } from "../../components/Plot";
import type { Limits, ProjectDetail } from "../../lib/api";
import { defaultElectrostatics, defaultHubbard, deviceSpec, electrostaticsFigure, esProblems, esSpec, GATE_SIDES, gatePreset,
  HUBBARD_SEEDS, negfDevice, type ElectrostaticsForm, type GateForm, type GateSide, type HubbardForm, type NegfMemory } from "../../lib/negf";

function Num({ label, value, onChange, unit, min, step, int = false, description }: {
  label: string; value: number; onChange: (v: number) => void; unit?: string; min?: number; step?: number; int?: boolean; description?: string;
}) {
  return <NumberInput label={label} description={description} size="xs" rightSection={unit ? <Text size="xs" c="dimmed">{unit}</Text> : undefined}
    rightSectionWidth={unit ? 36 : undefined} value={value} min={min} step={step} allowDecimal={!int} decimalScale={int ? 0 : 6}
    onChange={(v) => onChange(typeof v === "number" ? v : Number(v) || 0)} />;
}

function Range({ label, value, onChange, whole }: { label: string; value: number[] | null; onChange: (v: number[] | null) => void; whole: number }) {
  return (
    <Stack gap={2}>
      <Checkbox size="xs" label={`${label}: the whole device`} checked={!value}
        onChange={(e) => onChange(e.currentTarget.checked ? null : [Math.round(whole / 3), Math.round((2 * whole) / 3)])} />
      {value && (
        <Group grow gap={4}>
          <Num label="from" unit="Å" value={value[0]} onChange={(v) => onChange([v, value[1]])} />
          <Num label="to" unit="Å" value={value[1]} onChange={(v) => onChange([value[0], v])} />
        </Group>
      )}
    </Stack>
  );
}

function GateCard({ g, i, set, remove, length, width }: { g: GateForm; i: number; set: (g: GateForm) => void; remove: () => void; length: number; width: number }) {
  const plane = g.side === "below" || g.side === "above";
  return (
    <Card withBorder p="xs" data-testid={`gate-${i}`}>
      <Stack gap={6}>
        <Group justify="space-between" wrap="nowrap">
          <TextInput size="xs" value={g.name} onChange={(e) => set({ ...g, name: e.currentTarget.value })} aria-label="gate name" />
          <ActionIcon variant="subtle" color="red" onClick={remove} aria-label={`remove gate ${i + 1}`}>✕</ActionIcon>
        </Group>
        <Select size="xs" label="Where" data={GATE_SIDES} value={g.side} allowDeselect={false}
          onChange={(v) => set({ ...g, side: (v ?? "below") as GateSide })} />
        <SimpleGrid cols={3} spacing={4}>
          <Num label="Voltage" unit="V" step={0.1} value={g.voltage} onChange={(v) => set({ ...g, voltage: v })} />
          <Num label="Distance" unit="Å" min={0.5} value={g.distance} onChange={(v) => set({ ...g, distance: v })} />
          <Num label="Thickness" unit="Å" min={0.5} value={g.thickness} onChange={(v) => set({ ...g, thickness: v })} />
        </SimpleGrid>
        <Num label="εr between gate and device" description="0 = background (vacuum)" min={0} step={0.1}
          value={g.eps_r ?? 0} onChange={(v) => set({ ...g, eps_r: v > 0 ? v : null })} />
        <Range label="Along transport (x)" value={g.x} onChange={(x) => set({ ...g, x })} whole={length} />
        {plane && <Range label="Across (y)" value={g.y} onChange={(y) => set({ ...g, y })} whole={width} />}
      </Stack>
    </Card>
  );
}

function MagnetismCard({ hub, species, soc, set }: { hub: HubbardForm; species: string[]; soc: boolean; set: (h: HubbardForm) => void }) {
  const els = [...new Set(species)];
  return (
    <Card withBorder p="xs" data-testid="magnetism">
      <Stack gap={6}>
        <Switch size="xs" label="Magnetism: mean-field Hubbard U" checked={hub.enabled} disabled={soc}
          onChange={(e) => set({ ...hub, enabled: e.currentTarget.checked })} />
        {soc && <Text size="xs" c="dimmed">Not for spin–orbit fits (that needs non-collinear mean field).</Text>}
        {hub.enabled && (
          <>
            <SimpleGrid cols={Math.min(3, Math.max(1, els.length))} spacing={4}>
              {els.map((el) => (
                <Num key={el} label={`U (${el})`} unit="eV" min={0} step={0.1} value={hub.U[el] ?? 0}
                  onChange={(v) => set({ ...hub, U: { ...hub.U, [el]: v } })} />
              ))}
            </SimpleGrid>
            <Select size="xs" label="Starting state" data={HUBBARD_SEEDS} value={hub.seed} allowDeselect={false}
              onChange={(v) => set({ ...hub, seed: (v ?? "auto") as HubbardForm["seed"] })} />
            <Checkbox size="xs" label="Magnetic leads (solved first as the infinite ribbon)" checked={hub.lead_magnetism}
              onChange={(e) => set({ ...hub, lead_magnetism: e.currentTarget.checked })} />
            <Text size="xs" c="dimmed">Each orbital feels U times the other spin's extra occupation; e.g. U ≈ |t| for graphene gives
              antiferromagnetic edge moments on zigzag ribbons and opens a gap.</Text>
          </>
        )}
      </Stack>
    </Card>
  );
}

export function GatesStep({ project, mem, update, remember, limits, onBack, onNext }: {
  project: ProjectDetail; mem: NegfMemory; update: (m: Partial<NegfMemory>) => void; remember: (m: Partial<NegfMemory>) => void;
  limits: Limits; onBack: () => void; onNext: () => void;
}) {
  const es = mem.electrostatics ?? defaultElectrostatics();
  const set = (next: Partial<ElectrostaticsForm>) => update({ electrostatics: { ...es, ...next } });
  const spec = useMemo(() => ({ ...deviceSpec(mem.device), electrostatics: esSpec(es) }), [mem.device, es]);
  const [debounced] = useDebouncedValue(spec, 400);
  const geo = useQuery({
    queryKey: ["negf-device-es", mem.model_file, debounced],
    queryFn: () => negfDevice(mem.model_file!, debounced, project.id),
    enabled: !!mem.model_file,
    placeholderData: (prev) => prev,
  });
  const g = geo.data;
  const side = useMemo(() => (g?.electrostatics && !g.electrostatics.errors.length ? electrostaticsFigure(g, "side") : null), [g]);
  const top = useMemo(() => (g?.electrostatics && !g.electrostatics.errors.length ? electrostaticsFigure(g, "top") : null), [g]);
  const ext = g?.frame_extent;
  const length = ext ? ext[1][0] - ext[0][0] : 20;
  const width = ext ? ext[1][1] - ext[0][1] : 10;
  const problems = [...esProblems(es), ...(g?.electrostatics?.errors ?? [])];
  const maxGrid = Number(limits["negf.scf_max_grid_points"] ?? 0);
  const maxOrb = Number(limits["negf.scf_max_device_orbitals"] ?? 0);
  const gridTooBig = !!(maxGrid && (g?.electrostatics?.grid_points ?? 0) > maxGrid);
  const devTooBig = !!(maxOrb && (g?.orbitals ?? 0) > maxOrb);
  const setGate = (i: number, gate: GateForm) => set({ gates: es.gates.map((x, j) => (j === i ? gate : x)) });

  return (
    <Grid mt="md" gutter="lg">
      <Grid.Col span={{ base: 12, md: 4 }}>
        <Stack gap="sm">
          <Text size="sm" c="dimmed">For the self-consistent calculations (SC potential, SC I–V). The leads are metallic and neutral;
            gates hold their voltage; the potential and the charge are solved together (NEGF + Poisson).</Text>
          <SimpleGrid cols={2} spacing={6}>
            <Num label="Temperature" unit="K" min={1} value={es.temperature_K} onChange={(v) => set({ temperature_K: v })} />
            <Num label="Fermi level μ" unit="eV" step={0.05} value={es.mu} onChange={(v) => set({ mu: v })}
              description="relative to E_F of the fit" />
          </SimpleGrid>
          <Group gap={4}>
            <Text size="sm" fw={500}>Gates</Text>
            <Button size="compact-xs" variant="light" onClick={() => set({ gates: [...es.gates, ...gatePreset("back", length)] })}>+ back gate</Button>
            <Button size="compact-xs" variant="light" onClick={() => set({ gates: [...es.gates, ...gatePreset("finger", length)] })}>+ finger gate</Button>
            <Button size="compact-xs" variant="light" onClick={() => set({ gates: [...es.gates, ...gatePreset("split", length)] })}>+ split gates</Button>
          </Group>
          {es.gates.length === 0 && <Text size="xs" c="dimmed">No gates: the potential comes from the bias and the device's own charge.</Text>}
          {es.gates.map((gt, i) => (
            <GateCard key={i} g={gt} i={i} length={length} width={width} set={(x) => setGate(i, x)}
              remove={() => set({ gates: es.gates.filter((_, j) => j !== i) })} />
          ))}
          <MagnetismCard hub={es.hubbard ?? defaultHubbard(mem.summary?.species)} species={mem.summary?.species ?? []}
            soc={!!mem.summary?.soc} set={(hubbard) => set({ hubbard })} />
          <Accordion variant="contained">
            <Accordion.Item value="more">
              <Accordion.Control><Text size="sm">Grid, dielectric, doping, convergence</Text></Accordion.Control>
              <Accordion.Panel>
                <SimpleGrid cols={2} spacing={6}>
                  <Num label="Grid spacing" unit="Å" min={0.2} step={0.1} value={es.grid} onChange={(v) => set({ grid: v })} />
                  <Num label="Vacuum margin" unit="Å" min={0} value={es.vacuum} onChange={(v) => set({ vacuum: v })} />
                  <Num label="Background εr" min={1} step={0.1} value={es.eps_r} onChange={(v) => set({ eps_r: v })} />
                  <Num label="Doping" unit="e/atom" step={0.001} value={es.doping} onChange={(v) => set({ doping: v })} />
                  <Num label="Tolerance" unit="meV" min={0.01} step={0.1} value={es.tol_meV} onChange={(v) => set({ tol_meV: v })} />
                  <Num label="Max. iterations" int min={1} value={es.max_iter} onChange={(v) => set({ max_iter: v })} />
                  <Num label="Mixing" min={0.01} step={0.05} value={es.mixing} onChange={(v) => set({ mixing: v })} />
                  <Num label="Window step dE" unit="eV" min={0.0005} step={0.0005} value={es.dE} onChange={(v) => set({ dE: v })} />
                </SimpleGrid>
                <Checkbox mt="xs" size="xs" label="Electrostatics (Poisson) - untick for Hubbard U alone" checked={es.poisson !== false}
                  onChange={(e) => set({ poisson: e.currentTarget.checked })} />
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </Stack>
      </Grid.Col>
      <Grid.Col span={{ base: 12, md: 8 }}>
        <Stack gap="xs">
          {geo.error && <ErrorBox error={geo.error} />}
          {problems.map((p) => <Text key={p} size="sm" c="red">{p}</Text>)}
          {g?.electrostatics && !g.electrostatics.errors.length && (
            <Group gap="xs" data-testid="es-size">
              <Badge variant="light" color={devTooBig ? "red" : "blue"}>{g.orbitals?.toLocaleString()} orbitals
                {maxOrb ? ` of ${maxOrb.toLocaleString()} allowed (self-consistent)` : ""}</Badge>
              <Badge variant="light" color={gridTooBig ? "red" : "blue"}>grid {g.electrostatics.grid_shape?.join(" × ")} =
                {" "}{g.electrostatics.grid_points?.toLocaleString()} points{maxGrid ? ` of ${maxGrid.toLocaleString()}` : ""}</Badge>
            </Group>
          )}
          {devTooBig && <Alert color="orange">Self-consistent runs on your plan take devices up to {maxOrb.toLocaleString()} orbitals:
            make the device smaller in step 2.</Alert>}
          {gridTooBig && <Alert color="orange">The electrostatics grid is larger than your plan allows: use a larger grid spacing,
            less vacuum, or gates closer to the device.</Alert>}
          {side && <Plot data={side.data} layout={side.layout} height={300} />}
          {top && <Plot data={top.data} layout={top.layout} height={300} />}
        </Stack>
      </Grid.Col>
      <Grid.Col span={12}>
        <Group justify="space-between">
          <Button variant="default" onClick={onBack}>Back</Button>
          <Group gap="xs">
            <Button variant="subtle" onClick={onNext}>Skip (not self-consistent)</Button>
            <Button disabled={problems.length > 0} onClick={() => { remember({ electrostatics: es }); onNext(); }}>Next: contacts & fields</Button>
          </Group>
        </Group>
      </Grid.Col>
    </Grid>
  );
}
